/**
 * usePlannedOrders Hook
 * 
 * Manages planned orders for Mini-MRP Lite including:
 * - Fetching filtered planned orders from the database
 * - Status management (accept, cancel, convert)  
 * - Metrics calculation
 * - Real-time updates integration
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import { useAuthStore } from '@/store/authStore';
import { 
  PlannedOrder, 
  PlannedOrderStatus, 
  PlannedOrdersFilters,
  PlannedOrderMetrics,
  MRPSuggestionType 
} from '@/types/mrpLite';

export function usePlannedOrders() {
  const { user } = useAuthStore();
  const { toast } = useToast();
  
  const [plannedOrders, setPlannedOrders] = useState<PlannedOrder[]>([]);
  const [metrics, setMetrics] = useState<PlannedOrderMetrics>({
    total_orders: 0,
    pending_orders: 0,
    accepted_orders: 0,
    converted_orders: 0,
    cancelled_orders: 0,
    total_value: 0,
    pr_orders: 0,
    wo_orders: 0,
  });
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Fetch planned orders with optional filters
   */
  const fetchPlannedOrders = useCallback(async (filters?: PlannedOrdersFilters) => {
    try {
      setIsLoading(true);
      
      let query = supabase
        .from('planned_orders')
        .select(`
          *,
          item:item_id!inner(
            id,
            component_name,
            drawing_number,
            material,
            unit
          ),
          mrp_run:mrp_run_id!inner(
            id,
            run_number,
            created_at
          ),
          accepted_by_user:accepted_by!left(
            raw_user_meta_data
          ),
          converted_by_user:converted_by!left(
            raw_user_meta_data
          ),
          cancelled_by_user:cancelled_by!left(
            raw_user_meta_data
          )
        `)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }
      
      if (filters?.type && filters.type !== 'all') {
        query = query.eq('type', filters.type);
      }
      
      if (filters?.search_query) {
        query = query.or(`
          planned_order_number.ilike.%${filters.search_query}%,
          item.component_name.ilike.%${filters.search_query}%,
          item.drawing_number.ilike.%${filters.search_query}%
        `);
      }
      
      if (filters?.need_by_date_from) {
        query = query.gte('need_by_date', filters.need_by_date_from);
      }
      
      if (filters?.need_by_date_to) {
        query = query.lte('need_by_date', filters.need_by_date_to);
      }
      
      if (filters?.mrp_run_id) {
        query = query.eq('mrp_run_id', filters.mrp_run_id);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Transform data to match PlannedOrder interface
      const transformedOrders: PlannedOrder[] = (data || []).map(order => ({
        id: order.id,
        planned_order_number: order.planned_order_number,
        mrp_run_id: order.mrp_run_id,
        mrp_shortage_id: order.mrp_shortage_id,
        item_id: order.item_id,
        item_code: order.item?.drawing_number || 'N/A',
        item_name: order.item?.component_name || 'Unknown Item',
        item_description: order.item?.component_name || 'Unknown Item',
        uom: order.item?.unit || 'Nos',
        planned_qty: Number(order.planned_qty || order.suggested_qty || 0),
        need_by_date: order.need_by_date,
        order_date: order.order_date,
        type: (order.suggested_type || order.type) as MRPSuggestionType,
        status: order.status as PlannedOrderStatus,
        pr_id: order.pr_id,
        pr_number: order.pr_number,
        po_id: order.po_id,
        po_number: order.po_number,
        work_order_id: order.work_order_id,
        work_order_number: order.work_order_number,
        accepted_at: order.accepted_at,
        accepted_by_user_id: order.accepted_by,
        accepted_by_user_name: order.accepted_by_user?.raw_user_meta_data?.full_name || 'Unknown',
        converted_at: order.converted_at,
        converted_by_user_id: order.converted_by,
        converted_by_user_name: order.converted_by_user?.raw_user_meta_data?.full_name || 'Unknown',
        cancelled_at: order.cancelled_at,
        cancelled_by_user_id: order.cancelled_by,
        cancelled_by_user_name: order.cancelled_by_user?.raw_user_meta_data?.full_name || 'Unknown',
        cancellation_reason: order.cancellation_reason,
        created_at: order.created_at,
        updated_at: order.updated_at,
      }));

      setPlannedOrders(transformedOrders);
      
      // Calculate metrics
      const newMetrics: PlannedOrderMetrics = {
        total_orders: transformedOrders.length,
        pending_orders: transformedOrders.filter(o => o.status === 'PLANNED').length,
        accepted_orders: transformedOrders.filter(o => o.status === 'ACCEPTED').length,
        converted_orders: transformedOrders.filter(o => o.status === 'CONVERTED').length,
        cancelled_orders: transformedOrders.filter(o => o.status === 'CANCELLED').length,
        total_value: transformedOrders.reduce((sum, o) => sum + (o.planned_qty * 100), 0), // Placeholder value calculation
        pr_orders: transformedOrders.filter(o => o.type === 'PR').length,
        wo_orders: transformedOrders.filter(o => o.type === 'WO').length,
      };
      
      setMetrics(newMetrics);
      
    } catch (error) {
      console.error('Error fetching planned orders:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch planned orders',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  /**
   * Cancel a planned order
   */
  const cancelPlannedOrder = useCallback(async (orderId: string, reason?: string) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      setIsLoading(true);
      
      const { error } = await supabase
        .from('planned_orders')
        .update({
          status: 'CANCELLED',
          cancelled_at: new Date().toISOString(),
          cancelled_by: user.id,
          cancellation_reason: reason || 'Cancelled by user',
          updated_at: new Date().toISOString(),
        })
        .eq('id', orderId);

      if (error) throw error;

      // Update local state
      setPlannedOrders(prev => prev.map(order => 
        order.id === orderId 
          ? { 
              ...order, 
              status: 'CANCELLED' as PlannedOrderStatus,
              cancelled_at: new Date().toISOString(),
              cancelled_by_user_id: user.id,
              cancelled_by_user_name: (user as any).raw_user_meta_data?.full_name || 'Unknown',
              cancellation_reason: reason || 'Cancelled by user',
            }
          : order
      ));

      toast({
        title: 'Planned Order Cancelled',
        description: 'The planned order has been successfully cancelled',
        variant: 'default',
      });

    } catch (error) {
      console.error('Error cancelling planned order:', error);
      toast({
        title: 'Error',
        description: 'Failed to cancel planned order',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [user, toast]);

  /**
   * Accept a planned order (change status from PENDING to ACCEPTED)
   */
  const acceptPlannedOrder = useCallback(async (orderId: string) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      setIsLoading(true);
      
      const { error } = await supabase
        .from('planned_orders')
        .update({
          status: 'ACCEPTED',
          accepted_at: new Date().toISOString(),
          accepted_by: user.id,
          updated_at: new Date().toISOString(),
        })
        .eq('id', orderId)
        .eq('status', 'PLANNED'); // Only allow accepting planned orders

      if (error) throw error;

      // Update local state
      setPlannedOrders(prev => prev.map(order => 
        order.id === orderId 
          ? { 
              ...order, 
              status: 'ACCEPTED' as PlannedOrderStatus,
              accepted_at: new Date().toISOString(),
              accepted_by_user_id: user.id,
              accepted_by_user_name: (user as any).raw_user_meta_data?.full_name || 'Unknown',
            }
          : order
      ));

      toast({
        title: 'Planned Order Accepted',
        description: 'The planned order is now ready for conversion',
        variant: 'default',
      });

    } catch (error) {
      console.error('Error accepting planned order:', error);
      toast({
        title: 'Error',
        description: 'Failed to accept planned order',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [user, toast]);

  /**
   * Convert planned order to PR or WO
   */
  const convertPlannedOrder = useCallback(async (
    orderId: string,
    conversionType: 'PR' | 'WO',
    conversionData: {
      pr_id?: string;
      work_order_id?: string;
      po_id?: string;
    }
  ) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      setIsLoading(true);
      
      const updateData = {
        status: 'CONVERTED',
        converted_at: new Date().toISOString(),
        converted_by: user.id,
        updated_at: new Date().toISOString(),
        ...(conversionType === 'PR' && conversionData.pr_id && { pr_id: conversionData.pr_id }),
        ...(conversionType === 'WO' && conversionData.work_order_id && { work_order_id: conversionData.work_order_id }),
        ...(conversionData.po_id && { po_id: conversionData.po_id }),
      };

      const { error } = await supabase
        .from('planned_orders')
        .update(updateData)
        .eq('id', orderId)
        .eq('status', 'ACCEPTED'); // Only allow converting accepted orders

      if (error) throw error;

      // Update local state
      setPlannedOrders(prev => prev.map(order => 
        order.id === orderId 
          ? { 
              ...order, 
              status: 'CONVERTED' as PlannedOrderStatus,
              converted_at: new Date().toISOString(),
              converted_by_user_id: user.id,
              converted_by_user_name: (user as any).raw_user_meta_data?.full_name || 'Unknown',
              pr_id: updateData.pr_id || order.pr_id,
              work_order_id: updateData.work_order_id || order.work_order_id,
              po_id: updateData.po_id || order.po_id,
            }
          : order
      ));

      toast({
        title: 'Conversion Successful',
        description: `Planned order converted to ${conversionType} successfully`,
        variant: 'default',
      });

    } catch (error) {
      console.error('Error converting planned order:', error);
      toast({
        title: 'Error',
        description: 'Failed to convert planned order',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [user, toast]);

  return {
    plannedOrders,
    metrics,
    isLoading,
    fetchPlannedOrders,
    cancelPlannedOrder,
    acceptPlannedOrder,
    convertPlannedOrder,
  };
}
